import { Box } from "@mui/material";
import { AsyncState } from "@gui/widgets/AsyncState";
import type { PageLayoutProps } from "./PageLayout.types";
import { usePageLayoutViewModel } from "./PageLayout.vm";
import { PageBottomToolbar } from "./PageBottomToolbar";
import { PagePhoneActions } from "./PagePhoneActions";
import { PageTabs } from "./PageTabs";
import { isToolbarEmpty, PageTopToolbar } from "./PageTopToolbar";

/** Page gutters: 16px on a phone, 24px on desktop. */
const GUTTER_X = { xs: 2, md: 3 } as const;
/** The toolbars' padding above and below their row (12px). */
const TOOLBAR_PADDING = 1.5;
/** The column's cap per width. Each includes the gutters, so `form` leaves 440px for fields on desktop. */
const MAX_WIDTH = { full: undefined, narrow: 960, form: 488 } as const;
/**
 * `document` page with a bottom toolbar: at least the route container's height, with the body growing
 * to fill it, so the bar sits docked at the bottom of the screen even when the content is short
 * (owner decisions, 2026-10-20 desktop, 2026-10-21 phone).
 */
const DOCK_BOTTOM_ROOT = { display: "flex", flexDirection: "column", minHeight: "100%" } as const;
const DOCK_BOTTOM_BODY = { flexGrow: 1 } as const;
/** A flex column that fills its parent; `minHeight: 0` lets its own children scroll. */
const FILL_COLUMN = { flex: 1, minHeight: 0, display: "flex", flexDirection: "column" } as const;

/**
 * The page template. The title is not here: it lives in the app bar, from the route registry.
 *
 * Renders, top to bottom: optional page tabs, the optional toolbar, the body with standard
 * gutters, then the optional bottom toolbar (on a phone, the commit actions from either toolbar, in
 * the same docked strip). All of it sits inside the page's `<main>`, so jumping to
 * the main landmark lands on the page's own controls. With `async`, the body goes through
 * `AsyncState`. See
 * docs/design-system.md §4, "Page layout", for when to use each option.
 */
export function PageLayout<T extends string>(props: PageLayoutProps<T>) {
	const vm = usePageLayoutViewModel(props);
	const fill = props.scroll === "fill";
	// Header and body share one column, so the top toolbar lines up with the content under it.
	const column = {
		width: "100%",
		maxWidth: MAX_WIDTH[props.width ?? "full"],
		mx: "auto",
	} as const;
	const showToolbar = vm.topToolbar !== undefined && !isToolbarEmpty(vm.topToolbar);
	const hasHeader = Boolean(props.tabs) || showToolbar;
	// Desktop: the bottom toolbar. Phone: the commit actions, from either toolbar, in the same docked strip.
	const bottomBar = vm.showBottomToolbar
		? { testId: "page-bottom-toolbar", label: "Form actions" }
		: vm.phoneActions
			? { testId: "page-phone-actions", label: "Page actions" }
			: undefined;
	const dockBottomToolbar = bottomBar !== undefined && !fill;

	return (
		<Box
			component="main"
			data-testid="page-layout"
			// `fill`: exactly the route container's height. Its parent is a flex child with a definite
			// height, so 100% resolves.
			sx={
				fill
					? { height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }
					: dockBottomToolbar
						? DOCK_BOTTOM_ROOT
						: undefined
			}
		>
			{hasHeader && (
				<Box
					data-testid="page-header"
					sx={{
						flex: "none",
						bgcolor: "background.default",
						// `document` pages scroll under the header; it sticks to the top of the route's
						// scroll container. Above page content, below drawers and the app bar.
						...(fill ? {} : { position: "sticky", top: 0, zIndex: (theme) => theme.zIndex.appBar - 1 }),
					}}
				>
					{props.tabs && (
						<Box sx={{ borderBottom: 1, borderColor: "divider" }}>
							<Box sx={{ ...column, px: GUTTER_X }}>
								<PageTabs tabs={props.tabs} />
							</Box>
						</Box>
					)}
					{vm.topToolbar && showToolbar && (
						// A flush body has no gutter for the toolbar to line up with, so its padding is even all round.
						<Box sx={{ ...column, px: props.flush ? TOOLBAR_PADDING : GUTTER_X, py: TOOLBAR_PADDING }}>
							<PageTopToolbar config={vm.topToolbar} />
						</Box>
					)}
				</Box>
			)}
			<Box
				data-testid="page-body"
				aria-busy={props.async?.loading || undefined}
				sx={{
					...column,
					...(props.flush
						? { px: 0, pt: 0, pb: 0, ...(hasHeader ? { borderTop: 1, borderColor: "divider" } : {}) }
						: {
								px: GUTTER_X,
								// Under the top toolbar, its own bottom padding is the gap, matching the one above it; under
								// tabs alone, the body spaces itself off their divider; with no header, from the app bar.
								pt: showToolbar ? 0 : hasHeader ? 2 : { xs: 2, md: 3 },
								// A filling body scrolls inside itself; a strip under it is its edge, so no band between.
								pb: fill && bottomBar ? 0 : { xs: 2, md: 3 },
							}),
					...(fill ? FILL_COLUMN : {}),
					...(dockBottomToolbar ? DOCK_BOTTOM_BODY : {}),
				}}
			>
				{props.async ? <AsyncState {...props.async}>{props.children}</AsyncState> : props.children}
			</Box>
			{bottomBar && (
				<Box
					data-testid={bottomBar.testId}
					role="group"
					aria-label={bottomBar.label}
					sx={{
						flex: "none",
						bgcolor: "background.default",
						borderTop: 1,
						borderColor: "divider",
						// `document` pages scroll under it, so the commit stays in reach without a blank row
						// at the top.
						...(fill ? {} : { position: "sticky", bottom: 0, zIndex: (theme) => theme.zIndex.appBar - 1 }),
					}}
				>
					{vm.phoneActions ? (
						<Box sx={{ px: 1.5, py: 1.25 }}>
							<PagePhoneActions config={vm.phoneActions} />
						</Box>
					) : (
						props.bottomToolbar && (
							<Box sx={{ ...column, px: GUTTER_X, py: TOOLBAR_PADDING }}>
								<PageBottomToolbar config={props.bottomToolbar} />
							</Box>
						)
					)}
				</Box>
			)}
		</Box>
	);
}
